// PRD.305 §D4 — reusable guards for what may be done to a patient file.
//
// Files surfaces (Patient Files, Consultation Files, encounter deletion) each
// used to delete and edit rows unconditionally. A published laboratory result
// is a clinical artifact that must survive that (PRD §6.3), so the rule lives
// HERE — once, as domain logic — rather than as a lab-shaped `if` copied into
// every component.
//
// Deliberately generic: the guard takes a lock reason, not a laboratory row.
// A file with no lock behaves exactly as it always has, so non-laboratory file
// behaviour is unchanged by construction rather than by careful review.
//
// Dependency-free so it runs under the node:test harness.

/** Why a file cannot be modified. Extend as future artifact sources appear. */
export type PatientFileLockReason = 'published_lab_result';

export const PATIENT_FILE_LOCK_MESSAGES: Record<PatientFileLockReason, string> = {
  published_lab_result:
    'This is a published laboratory result and is part of the clinical record. Publish a correction from Labs instead.',
};

/**
 * The set of locked files for a patient, keyed by `patient_files.id`.
 * Built once per surface from `qLockedPatientFileIds` and passed down, so a
 * list of 200 files costs one lookup each instead of 200 queries.
 */
export type PatientFileLocks = ReadonlyMap<string, PatientFileLockReason>;

/** An empty lock set — the correct default while a query is still loading. */
export const NO_PATIENT_FILE_LOCKS: PatientFileLocks = new Map();

/** Row shape returned by `qLockedPatientFileIds`. */
export interface LockedPatientFileRow {
  patient_file_id?: string | null;
}

/**
 * Build the lock map from query rows. Every row the query returns is by
 * definition a published lab artifact — the filtering happens in SQL — so this
 * only has to shape the result.
 */
export function buildPatientFileLocks(rows: readonly LockedPatientFileRow[]): PatientFileLocks {
  const locks = new Map<string, PatientFileLockReason>();
  for (const row of rows) {
    if (row.patient_file_id) locks.set(row.patient_file_id, 'published_lab_result');
  }
  return locks;
}

export interface PatientFilePermissions {
  ok: boolean;
  reason?: string;
  lockReason?: PatientFileLockReason;
}

const ALLOWED: PatientFilePermissions = { ok: true };

function locked(reason: PatientFileLockReason): PatientFilePermissions {
  return { ok: false, reason: PATIENT_FILE_LOCK_MESSAGES[reason], lockReason: reason };
}

/** Whether a file is locked at all (drives the badge / lock icon in Files). */
export function patientFileLockReason(
  fileId: string,
  locks: PatientFileLocks = NO_PATIENT_FILE_LOCKS,
): PatientFileLockReason | null {
  return locks.get(fileId) ?? null;
}

/**
 * May this file be deleted? A published laboratory artifact may not — deleting
 * it would remove a published result from Labs, Consultation and Files at once.
 */
export function canDeletePatientFile(
  fileId: string,
  locks: PatientFileLocks = NO_PATIENT_FILE_LOCKS,
): PatientFilePermissions {
  const reason = patientFileLockReason(fileId, locks);
  return reason ? locked(reason) : ALLOWED;
}

/**
 * May this file's metadata (record date, linked consultation) be edited?
 * Same rule: a published artifact's provenance is part of the record.
 */
export function canEditPatientFile(
  fileId: string,
  locks: PatientFileLocks = NO_PATIENT_FILE_LOCKS,
): PatientFilePermissions {
  const reason = patientFileLockReason(fileId, locks);
  return reason ? locked(reason) : ALLOWED;
}

/**
 * Partition a set of file ids into deletable and protected. Used by bulk paths
 * — notably encounter deletion, which must remove an encounter's ordinary files
 * while leaving its published laboratory results intact.
 */
export function partitionDeletablePatientFiles(
  fileIds: readonly string[],
  locks: PatientFileLocks = NO_PATIENT_FILE_LOCKS,
): { deletable: string[]; protected: string[] } {
  const deletable: string[] = [];
  const protectedIds: string[] = [];
  for (const id of fileIds) {
    (patientFileLockReason(id, locks) ? protectedIds : deletable).push(id);
  }
  return { deletable, protected: protectedIds };
}
